import { useEffect, useRef, useCallback } from "react";
import { X, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

export const SlidePanel = ({ open, onClose, title, children, items, currentIndex, onNavigate }) => {
  const panelRef = useRef(null);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const handleKeyDown = useCallback((e) => {
    if (!open) return;
    if (e.key === "Escape") { onClose(); return; }
    if (!items || !onNavigate || typeof currentIndex !== "number") return;
    if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      if (currentIndex > 0) onNavigate(currentIndex - 1);
    } else if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      if (currentIndex < items.length - 1) onNavigate(currentIndex + 1);
    }
  }, [open, onClose, items, currentIndex, onNavigate]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  if (!open) return null;

  const hasNav = items && onNavigate && typeof currentIndex === "number" && items.length > 1;

  return (
    <div className="fixed inset-0 z-50" data-testid="slide-panel-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" />
      {/* Panel */}
      <div
        ref={panelRef}
        className="absolute top-0 right-0 h-full w-full max-w-[520px] bg-card border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
        data-testid="slide-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
          <h2 className="text-lg font-serif text-foreground truncate pr-4">{title}</h2>
          <div className="flex items-center gap-1 shrink-0">
            {hasNav && (
              <>
                <Button
                  variant="ghost" size="icon" className="h-7 w-7"
                  disabled={currentIndex <= 0}
                  onClick={() => onNavigate(currentIndex - 1)}
                  data-testid="slide-panel-prev"
                  title="Previous (Arrow Up)"
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <span className="text-xs text-muted-foreground tabular-nums min-w-[3ch] text-center">{currentIndex + 1}/{items.length}</span>
                <Button
                  variant="ghost" size="icon" className="h-7 w-7"
                  disabled={currentIndex >= items.length - 1}
                  onClick={() => onNavigate(currentIndex + 1)}
                  data-testid="slide-panel-next"
                  title="Next (Arrow Down)"
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8 ml-1" onClick={onClose} data-testid="slide-panel-close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {children}
        </div>
      </div>
    </div>
  );
};
